'use client'

/**
 * useAssistantWidgetState — shared launcher state for the assistant's two
 * entry points: the desktop floating widget (AssistantWidget) and the mobile
 * bottom strip (MobileAssistantChat). Both render their own chrome around
 * this hook, which owns:
 *
 *   - the persisted active conversation id (assistant_conversation_id),
 *     hydrated after mount so SSR and the hydration render agree
 *   - the stale-conversation reset when the persisted id is no longer
 *     accessible to the current user/org
 *   - the unread-dot derivation (assistant_last_seen_message_id)
 *   - the streaming flag for launcher affordances
 *
 * localStorage keys are shared across both entry points on purpose: the user
 * resumes the same conversation whether they open the chat on desktop or on
 * a phone.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAssistant } from './useAssistant'
import type { Id } from '../../../convex/_generated/dataModel'

export const LS_OPEN_KEY = 'assistant_open'
export const LS_CONV_KEY = 'assistant_conversation_id'
export const LS_SEEN_KEY = 'assistant_last_seen_message_id'
export const LS_EXPANDED_KEY = 'assistant_expanded'

export function readLS(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    // Also swallows the SSR ReferenceError (no window) — callers treat
    // "unavailable" and "unset" the same way.
    return null
  }
}

export function writeLS(key: string, value: string | null) {
  try {
    if (value == null) {
      window.localStorage.removeItem(key)
    } else {
      window.localStorage.setItem(key, value)
    }
  } catch {
    // Ignore storage errors (private browsing, quota exceeded).
  }
}

export function useAssistantWidgetState({ isOpen }: { isOpen: boolean }) {
  const [mounted, setMounted] = useState(false)
  const [activeConversationId, setActiveConversationId] =
    useState<Id<'assistantConversations'> | null>(null)

  // Hydrate the persisted conversation id after mount to avoid SSR mismatch.
  useEffect(() => {
    setMounted(true)
    const storedConvId = readLS(LS_CONV_KEY)
    if (storedConvId) {
      setActiveConversationId(storedConvId as Id<'assistantConversations'>)
    }
  }, [])

  const onConversationChange = useCallback(
    (id: Id<'assistantConversations'> | null) => {
      setActiveConversationId(id)
      writeLS(LS_CONV_KEY, id ?? null)
    },
    [],
  )

  // Convex dedupes duplicate useQuery calls — safe to call here AND inside
  // AssistantPanel simultaneously.
  const { conversationData, isStreaming } = useAssistant({ activeConversationId })

  // Reset a stale active conversation. getConversation returns `undefined` while
  // loading and `null` when the conversation is not accessible to the current
  // user/org (different org, archived, deleted). The id is persisted in
  // localStorage, so after an org switch or operator impersonation change it can
  // point at a conversation owned by the previous org — sending to it would throw
  // "conversation belongs to a different organization". Drop back to a draft so
  // the next send creates a fresh conversation in the current org.
  useEffect(() => {
    if (activeConversationId != null && conversationData === null) {
      onConversationChange(null)
    }
  }, [activeConversationId, conversationData, onConversationChange])

  // Unread dot logic.
  const [lastSeenId, setLastSeenId] = useState<string | null>(() => readLS(LS_SEEN_KEY))

  // The latest completed assistant message id in the active conversation.
  const latestAssistantMessageId = (() => {
    if (!conversationData?.messages) return null
    const assistantMessages = conversationData.messages.filter(
      (m) => m.role === 'assistant' && m.status === 'complete',
    )
    if (assistantMessages.length === 0) return null
    return assistantMessages[assistantMessages.length - 1]._id
  })()

  // When the chat is open and there are messages, mark them as seen.
  useEffect(() => {
    if (isOpen && latestAssistantMessageId) {
      setLastSeenId(latestAssistantMessageId)
      writeLS(LS_SEEN_KEY, latestAssistantMessageId)
    }
  }, [isOpen, latestAssistantMessageId])

  const showUnreadDot =
    !isOpen &&
    latestAssistantMessageId != null &&
    latestAssistantMessageId !== lastSeenId

  return {
    mounted,
    activeConversationId,
    onConversationChange,
    isStreaming,
    showUnreadDot,
  }
}
